/**
 * "When to ask a person" and "Who you are actually looking for" — Story 20.4.
 *
 * Visual Reference: `design/screens/editor-qualify.html`. The threshold card sits
 * beneath Bands in the rubric column, as the M2 prompt placed it ("directly
 * beneath the bands section"); the ICP card sits in the right-hand column after
 * the answer probe.
 *
 * THE THRESHOLD SAYS WHAT HAPPENS, NOT JUST WHERE. AC 3: the copy states what
 * happens below the number — a lead goes to a person whatever its score — and the
 * sentence follows the number as it moves, naming this funnel's own top band.
 * The drawn "About 6 in 100 … last month" line is not rendered: nothing can count
 * it until Epic 22's queue exists (Q-104).
 *
 * TWO CONTROLS, ONE VALUE. The export draws a range and a percent field side by
 * side; both write through the same recipe, so they cannot disagree.
 *
 * The card shell repeats QualifyTab's `Card` — same classes, drawn the same way —
 * rather than importing it, because QualifyTab imports this file.
 */
import { useId, useState } from 'react'
import { ICP_PROFILE_MAX, type Funnel } from '@leadgible/schema'
import { DRAWN, LABELS, consequence, icpCount } from './escalation-copy'
import { icpRefusal, setConfidenceThreshold, setIcpProfile, thresholdOf, topBandLabel } from './escalation'
import { edit } from './store'

const CARD =
  'flex flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card'

export function ThresholdCard({ funnel }: { funnel: Funnel }) {
  const threshold = thresholdOf(funnel)

  const change = (raw: string) =>
    edit(
      'Change the confidence threshold',
      (draft) => setConfidenceThreshold(draft, Number(raw)),
      // A dragged slider is one change, not forty undo steps.
      { coalesce: true },
    )

  return (
    <section className={CARD}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{DRAWN.thresholdTitle}</span>
        <span className="text-mdx-13 text-mdx-slate">{DRAWN.thresholdAside}</span>
      </div>

      <div className="flex flex-wrap items-center gap-mdx-16">
        {/*
          The export gives the slider `flex: 1 1 220px; min-width: 160px`. The
          basis decides where the row WRAPS — at 375 the drawn slider takes its
          own line and the percent field goes beneath it — so it borrows
          `--mdx-filter-column`, the one 220px product token (a filter-select
          column by role, borrowed for its value). Without a basis the slider shrank to a sliver
          beside the field, which the 375 screenshot showed and no assertion
          could. The 160px minimum has no token and is not needed once the basis
          wraps the row.
        */}
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          aria-label={LABELS.thresholdSlider}
          value={threshold}
          onChange={(event) => change(event.target.value)}
          className="h-mdx-24 min-w-0 flex-1 basis-mdx-filter-column cursor-pointer accent-mdx-brand"
        />
        <span className="flex flex-none items-center gap-mdx-8">
          {/*
            84px, as drawn: `--mdx-meta-column-sm`, the numeric slot DECISION-147
            named for it. Story 20.4 built it at `--mdx-meta-column`'s 96px while
            no token named 84, and Story 20.8 moved it with the weight inputs and
            the share bar one card up.
          */}
          <input
            type="number"
            min={0}
            max={100}
            aria-label={LABELS.thresholdPercent}
            value={threshold}
            onChange={(event) => change(event.target.value)}
            className="h-mdx-input w-mdx-meta-column-sm rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-10 text-mdx-15 font-mdx-semibold text-mdx-ink outline-none tabular-nums focus:border-mdx-brand"
          />
          <span className="text-mdx-15 text-mdx-slate">{DRAWN.percent}</span>
        </span>
      </div>

      <p className="m-0 max-w-mdx-measure-60 text-mdx-16">
        {consequence(threshold, topBandLabel(funnel))}
      </p>
    </section>
  )
}

export function IcpCard({ funnel }: { funnel: Funnel }) {
  const refusalId = useId()
  const stored = funnel.rubric.icp_profile ?? ''
  /*
    STORY 20.9: EMPTYING THE DESCRIPTION WHILE A CRITERION READS THE FIT IS
    REFUSED BEFORE ANYTHING IS SAVED. The emptied text is HELD here — shown, as
    the capture draws the card at "0 / 4,000" — and never written, so the draft
    keeps the description the criterion scores against. The sentence is
    recomputed from the funnel, so removing the criterion lifts the refusal, and
    the card shows the description the draft still has.
  */
  const [held, setHeld] = useState<string | null>(null)
  const refusal = held === null ? null : icpRefusal(funnel, held)
  const text = refusal !== null && held !== null ? held : stored

  return (
    <section className={CARD}>
      <div className="flex flex-col gap-mdx-6">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{DRAWN.icpTitle}</span>
        <span className="max-w-mdx-measure-60 text-mdx-15 text-mdx-slate">{DRAWN.icpLead}</span>
      </div>
      <textarea
        aria-label={LABELS.icp}
        aria-invalid={refusal !== null ? true : undefined}
        aria-describedby={refusal !== null ? refusalId : undefined}
        value={text}
        maxLength={ICP_PROFILE_MAX}
        rows={4}
        onChange={(event) => {
          const next = event.target.value
          if (icpRefusal(funnel, next) !== null) {
            setHeld(next)
            return
          }
          setHeld(null)
          edit('Describe your ideal customer', (draft) => setIcpProfile(draft, next), { coalesce: true })
        }}
        className={[
          'min-h-mdx-textarea-min resize-y rounded-mdx-control border bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 leading-mdx-body text-mdx-ink outline-none',
          refusal !== null ? 'border-mdx-error' : 'border-mdx-line focus:border-mdx-brand',
        ].join(' ')}
      />
      {refusal !== null ? (
        // As drawn: the refusal and the counter share a line, the refusal
        // flexing from 240px (`--mdx-sidebar-width`, borrowed for its value).
        <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
          <span id={refusalId} role="alert" className="min-w-0 flex-1 basis-mdx-sidebar text-mdx-13 text-mdx-error">
            {refusal}
          </span>
          <span className="flex-none text-mdx-13 text-mdx-slate tabular-nums">
            {icpCount(text.length, ICP_PROFILE_MAX)}
          </span>
        </div>
      ) : (
        <span className="self-end text-mdx-13 text-mdx-slate tabular-nums">
          {icpCount(text.length, ICP_PROFILE_MAX)}
        </span>
      )}
    </section>
  )
}
